import { observable, action, computed, decorate } from "mobx";

const WIDTH_KEY = "sg_copilot_drawer_width";
const PINNED_KEY = "sg_copilot_drawer_pinned";
const OPEN_KEY = "sg_copilot_drawer_open";

export const DRAWER_MIN_WIDTH = 320;
export const DRAWER_MAX_WIDTH = 640;
export const DEFAULT_DRAWER_WIDTH = 400;
// Below this a docked drawer leaves the page too narrow to work in even at the
// 40% cap, so pinning is refused and the drawer floats instead. Kept low: with
// devtools docked the viewport shrinks well below the window, and a threshold
// that takes Pin away exactly when the screen gets tight is the wrong trade.
export const PIN_MIN_VIEWPORT = 860;

const readNumber = (key, fallback) => {
  const raw = parseInt(localStorage.getItem(key), 10);
  return Number.isFinite(raw) ? raw : fallback;
};

const clampWidth = value =>
  Math.min(DRAWER_MAX_WIDTH, Math.max(DRAWER_MIN_WIDTH, value));

/*
 * Presentation state for the sequence copilot drawer. The conversation itself
 * lives in copilotStore -- this only owns where the panel sits, so the drawer
 * and the full /copilot page stay one conversation with two containers.
 */
class CopilotDrawerStore {
  // Restored across reloads so the copilot stays where the user left it. Only
  // an explicit open is remembered -- a user who never opened it still gets a
  // closed drawer on first load.
  open = localStorage.getItem(OPEN_KEY) === "true";
  // Findings render above the thread until the user says something; once the
  // conversation starts they would just be stale noise above live answers.
  surface = "";
  entityId = "";
  width = clampWidth(readNumber(WIDTH_KEY, DEFAULT_DRAWER_WIDTH));
  // Docked by default: the drawer is meant to sit beside the page, not over it.
  // Only an explicit unpin turns that off.
  pinnedPref = localStorage.getItem(PINNED_KEY) !== "false";
  viewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
  // Findings the CURRENT page computed about itself. Published here rather
  // than passed as a prop because the drawer is mounted once for the whole
  // app now -- the page that can compute them is no longer its parent.
  pageFindings = [];
  // The key of whoever last published a non-empty list -- lets a second
  // page sharing this screen publish [] on unmount without wiping the
  // findings the other one just set (see setPageFindings).
  pageFindingsOwner = "";

  get canPin() {
    return this.viewportWidth >= PIN_MIN_VIEWPORT;
  }

  // Pinning is a preference, not a promise -- a narrow window overrides it
  // without discarding what the user asked for.
  get pinned() {
    return this.pinnedPref && this.canPin;
  }

  /**
   * Width actually used. Pinned, the drawer takes space from the page, and past
   * roughly 40% the app header runs out of room and its contents start to
   * collide -- so the stored preference is capped rather than obeyed blindly.
   * Floating overlays the page and can be any size the user dragged it to.
   */
  get effectiveWidth() {
    if (!this.pinned) return this.width;
    return Math.min(this.width, Math.round(this.viewportWidth * 0.4));
  }

  openFor = (surface, entityId = "") => {
    this.surface = surface;
    this.entityId = entityId;
    // Resize is the only other source, and devtools can have opened since the
    // last one -- re-read now so canPin reflects the width we actually have.
    if (typeof window !== "undefined") this.viewportWidth = window.innerWidth;
    this.setOpen(true);
  };

  close = () => {
    this.setOpen(false);
  };

  setOpen = value => {
    this.open = !!value;
    localStorage.setItem(OPEN_KEY, String(this.open));
  };

  toggle = (surface, entityId = "") => {
    if (this.open) this.close();
    else this.openFor(surface, entityId);
  };

  setPinned = value => {
    this.pinnedPref = !!value;
    localStorage.setItem(PINNED_KEY, String(this.pinnedPref));
  };

  setWidth = value => {
    this.width = clampWidth(Math.round(value));
    localStorage.setItem(WIDTH_KEY, String(this.width));
  };

  setPageFindings = (list, key = "page") => {
    const items = Array.isArray(list) ? list : [];
    // A non-owner publishing an empty list (e.g. a list view whose detail
    // sibling now owns the drawer) is a no-op -- it must not clear what the
    // owner just set.
    if (!items.length && this.pageFindingsOwner && key !== this.pageFindingsOwner) {
      return;
    }
    this.pageFindings = items;
    this.pageFindingsOwner = items.length ? key : "";
  };

  clearPageFindings = key => {
    if (key !== this.pageFindingsOwner) return;
    this.pageFindings = [];
    this.pageFindingsOwner = "";
  };

  setViewportWidth = value => {
    this.viewportWidth = value;
  };

  setReturnTo = path => {
    this.returnTo = path || "";
  };

  consumeReturnTo = () => {
    const path = this.returnTo;
    this.returnTo = "";
    return path;
  };

  requestReopen = ({ surface, entityId, conversationId, keepAlive = false } = {}) => {
    this.pendingReopen = { surface, entityId, conversationId, keepAlive };
  };

  consumeReopen = () => {
    const pending = this.pendingReopen;
    this.pendingReopen = null;
    return pending;
  };
}

decorate(CopilotDrawerStore, {
  open: observable,
  surface: observable,
  entityId: observable,
  width: observable,
  pinnedPref: observable,
  viewportWidth: observable,
  pageFindings: observable,
  pageFindingsOwner: observable,
  returnTo: observable,
  pendingReopen: observable,
  canPin: computed,
  effectiveWidth: computed,
  pinned: computed,
  openFor: action,
  close: action,
  setOpen: action,
  toggle: action,
  setPinned: action,
  setWidth: action,
  setPageFindings: action,
  clearPageFindings: action,
  setViewportWidth: action,
  setReturnTo: action,
  consumeReturnTo: action,
  requestReopen: action,
  consumeReopen: action
});

const copilotDrawerStore = new CopilotDrawerStore();
export default copilotDrawerStore;
